import { useEffect } from 'react';
import { useIsPhone } from '@internal/plugin-waadoo-core';

/**
 * Tab rows that scroll sideways on a phone, every one of them at once.
 *
 * A row is the element that scrolls: BUI Tabs' list wrapper
 * (`.bui-TabListWrapper`, whatever the page — the branch page, the project
 * page's header, « Créer », the catalog index, « Tâches », settings and the
 * visualizer) or BUI Header's tab bar (`.bui-HeaderNav`, the catalog's
 * entity pages, drawn by plugin-catalog, out of our reach). styles.css lets
 * both scroll (`overflow-x: auto`) and, on a phone, hides their scrollbar
 * and fades the edge that has more behind it — read from the two flags set
 * here:
 *  - `data-waadoo-more-start` / `data-waadoo-more-end`: present while tabs
 *    are hidden past the left / right edge;
 * and brings the selected tab into view when the row appears and whenever
 * the selection changes (a tap, a link, the browser's back button).
 *
 * Watched from the document rather than wired into each row, since some
 * rows are rendered by upstream plugins. Only while the window is a phone's
 * (`PHONE_MEDIA_QUERY`, waadoo-core): on a larger screen nothing is observed and
 * nothing is set.
 */

const ROW_SELECTOR = '.bui-TabListWrapper, .bui-HeaderNav';

/** A row's tabs: BUI's (react-aria), or the items of BUI Header's bar. */
const TAB_SELECTOR = `[role='tab'], .bui-HeaderNavItem, .bui-HeaderNavGroup`;

/** BUI's selected tab (react-aria), or the current item of a nav bar. */
const SELECTED_SELECTOR = `[role='tab'][data-selected='true'], [aria-current='page']`;

/** Room kept between the selected tab and the edge: the fade's width. */
export const EDGE_ROOM_PX = 24;

const MORE_START = 'data-waadoo-more-start';
const MORE_END = 'data-waadoo-more-end';

function setFlag(element: Element, name: string, on: boolean) {
  if (on) element.setAttribute(name, '');
  else element.removeAttribute(name);
}

/** Sets the row's two flags from where it is scrolled. */
export function updateRowEdges(row: HTMLElement): void {
  const hidden = row.scrollWidth - row.clientWidth;
  // A pixel of slack: scrollLeft is fractional on zoomed or high-DPI
  // screens and may stop a fraction short of either end.
  setFlag(row, MORE_START, row.scrollLeft > 1);
  setFlag(row, MORE_END, row.scrollLeft < hidden - 1);
}

/**
 * Scrolls the row, and the row only, so that its selected tab is shown,
 * clear of the fades: `scrollIntoView` would scroll the page as well.
 * Whether the row has a selected tab yet.
 */
export function revealSelectedTab(
  row: HTMLElement,
  behavior: ScrollBehavior,
): boolean {
  const tab = row.querySelector(SELECTED_SELECTOR);
  if (!tab) return false;
  const box = row.getBoundingClientRect();
  const target = tab.getBoundingClientRect();
  let delta = 0;
  if (target.left < box.left + EDGE_ROOM_PX) {
    delta = target.left - box.left - EDGE_ROOM_PX;
  } else if (target.right > box.right - EDGE_ROOM_PX) {
    delta = target.right - box.right + EDGE_ROOM_PX;
  }
  if (delta !== 0) {
    row.scrollTo({ left: row.scrollLeft + delta, behavior });
  }
  return true;
}

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Follows one row until `stop`; its flags are removed then. */
function watchRow(row: HTMLElement): () => void {
  const update = () => updateRowEdges(row);
  row.addEventListener('scroll', update, { passive: true });
  // The selected tab is brought into view at once the first time — BUI
  // may mark it only after its first render — then smoothly when the
  // selection moves: BUI moves `data-selected`, a nav bar `aria-current`.
  let shown = false;
  const reveal = () => {
    const behavior = !shown || reducedMotion() ? 'auto' : 'smooth';
    shown = revealSelectedTab(row, behavior) || shown;
  };
  // What is hidden changes with the row's width (rotation) and its tabs'
  // (the web font loaded after the first layout, a badge): the tab list
  // itself keeps the row's width whatever overflows it, so the tabs are
  // observed one by one. The selected tab may have moved out of view: it is
  // brought back, at once.
  const resize = new ResizeObserver(() => {
    if (shown) revealSelectedTab(row, 'auto');
    update();
  });
  resize.observe(row);
  for (const tab of Array.from(row.querySelectorAll(TAB_SELECTOR))) {
    resize.observe(tab);
  }
  const selection = new MutationObserver(reveal);
  selection.observe(row, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-selected', 'aria-current'],
  });

  reveal();
  update();

  return () => {
    row.removeEventListener('scroll', update);
    resize.disconnect();
    selection.disconnect();
    row.removeAttribute(MORE_START);
    row.removeAttribute(MORE_END);
  };
}

/**
 * Follows every tab row of `root` (rows added later included) until the
 * returned function is called.
 */
export function watchTabRows(root: HTMLElement): () => void {
  const rows = new Map<HTMLElement, () => void>();
  // On any change of the tree: rows gone are dropped, new rows followed,
  // and the others' flags read again (a tab added, a label changed).
  const sync = () => {
    for (const [row, stop] of rows) {
      if (row.isConnected) {
        updateRowEdges(row);
      } else {
        stop();
        rows.delete(row);
      }
    }
    for (const row of Array.from(
      root.querySelectorAll<HTMLElement>(ROW_SELECTOR),
    )) {
      if (!rows.has(row)) rows.set(row, watchRow(row));
    }
  };
  // At most once a frame: a busy page (streaming logs) changes the tree
  // far more often than that.
  let frame: number | undefined;
  const tree = new MutationObserver(() => {
    if (frame !== undefined) return;
    frame = window.requestAnimationFrame(() => {
      frame = undefined;
      sync();
    });
  });
  tree.observe(root, { childList: true, subtree: true });
  sync();
  return () => {
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    tree.disconnect();
    for (const stop of rows.values()) stop();
    rows.clear();
  };
}

/** {@link watchTabRows} on the document, while the window is a phone's. */
export function useTabRowScroll(): void {
  const phone = useIsPhone();
  useEffect(() => (phone ? watchTabRows(document.body) : undefined), [phone]);
}

/** The app root element that runs {@link useTabRowScroll}; draws nothing. */
export function TabRowScroll(): null {
  useTabRowScroll();
  return null;
}
